/* eslint-disable max-classes-per-file */
// Event logging data structures and management classes
// This file contains all event type definitions and Log classes for managing event data

// Import compact format types and encoders
import type {
  CompactKeyboardEvent,
  CompactMouseEvent,
  CompactWheelEvent,
  CompactTouchEvent,
  CompactFocusEvent,
  CompactFormEvent,
  CompactClipboardEvent,
  CompactSelectionEvent,
  CompactViewportEvent,
  CompactCompositionEvent,
  CompactDeviceOrientationEvent,
  CompactErrorEvent,
} from './compactEventFormat';
import {
  encodeKeyboardEvent,
  encodeMouseEvent,
  encodeWheelEvent,
  encodeTouchEvent,
  encodeFocusEvent,
  encodeFormEvent,
  encodeClipboardEvent,
  encodeSelectionEvent,
  encodeViewportEvent,
  encodeCompositionEvent,
  encodeDeviceOrientationEvent,
  encodeErrorEvent,
  trimTrailingNulls,
} from './compactEventFormat';

// Shared interfaces
export interface ElementBounds {
  x: number;
  y: number;
  width: number;
  height: number;
}

// Event type definitions

/**
 * Captures keyboard interactions when users press or release keys.
 * Triggered by any keyboard input including typing, navigation keys (arrows, tab),
 * function keys, modifier keys (ctrl, shift, alt), and special keys (escape, enter).
 * The 'repeat' field indicates if a key is being held down and auto-repeating.
 */
export interface KeyboardEvent {
  type: 'keydown' | 'keyup';
  timestamp: number;
  key: string; // The actual character or key name (e.g., 'a', 'Enter', 'ArrowLeft')
  code: string; // Physical key code (e.g., 'KeyA', 'Enter', 'ArrowLeft')
  element_index: number; // Index into ElementLog array
  modifiers: string[]; // Active modifier keys like 'ctrl', 'shift', 'alt', 'meta'
  repeat: boolean; // True if key is being held down and auto-repeating
}

/**
 * Captures various mouse interactions and cursor movements.
 * Triggered by clicking, moving the mouse, scrolling, right-clicking for context menus,
 * and mouse entering/leaving elements. The 'mouseleave' and 'mouseenter' events are
 * useful for tracking hover behaviors and element interaction patterns.
 */
export interface MouseEvent {
  type:
    | 'mousedown' // Mouse button pressed down
    | 'mouseup' // Mouse button released
    | 'click' // Complete click (down + up)
    | 'mousemove' // Mouse cursor movement
    | 'scroll' // Scrolling within an element
    | 'contextmenu' // Right-click context menu
    | 'mouseleave' // Mouse cursor leaves an element
    | 'mouseenter'; // Mouse cursor enters an element
  timestamp: number;
  x: number; // Cursor position relative to element
  y: number; // Cursor position relative to element
  button?: number; // Mouse button: 0=left, 1=middle, 2=right
  element_index: number; // Index into ElementLog array
  scroll_top?: number; // Vertical scroll position (for scroll events)
  scroll_left?: number; // Horizontal scroll position (for scroll events)
}

/**
 * Captures mouse wheel and trackpad scrolling interactions.
 * Triggered by mouse wheel rotation, trackpad two-finger scrolling, or similar
 * scrolling gestures. Different from regular scroll events as it captures the
 * raw wheel/trackpad input with precise delta values and scroll modes.
 */
export interface WheelEvent {
  type: 'wheel';
  timestamp: number;
  x: number; // Cursor position when wheel event occurred
  y: number; // Cursor position when wheel event occurred
  deltaX: number; // Horizontal scroll amount
  deltaY: number; // Vertical scroll amount (most common)
  deltaZ: number; // Z-axis scroll (rarely used, for 3D scrolling)
  deltaMode: number; // Scroll unit: 0=pixels, 1=lines, 2=pages
  element_index: number; // Index into ElementLog array
}

/**
 * Captures clipboard operations when users copy, cut, or paste content.
 * Triggered by Ctrl+C/Cmd+C (copy), Ctrl+X/Cmd+X (cut), Ctrl+V/Cmd+V (paste),
 * or using right-click context menu clipboard options. Important for tracking
 * data entry patterns and content manipulation behaviors.
 */
export interface ClipboardEvent {
  type: 'copy' | 'paste' | 'cut';
  timestamp: number;
  element_index: number; // Index into ElementLog array
  data_length?: number; // Length of clipboard data (for privacy, not the actual content)
}

/**
 * Captures text selection events when users highlight or select content.
 * Triggered by dragging to select text, double-clicking to select words,
 * triple-clicking to select paragraphs, Ctrl+A/Cmd+A to select all, or using
 * shift+arrow keys to extend selections. These events help understand reading
 * patterns and content interaction behaviors.
 */
export interface SelectionEvent {
  type: 'selectstart' | 'selectend' | 'selectionchange';
  timestamp: number;
  element_index: number; // Index into ElementLog array
  selection_length?: number; // Number of characters selected
  selection_start?: number; // Start position of selection in text
  selection_end?: number; // End position of selection in text
}

/**
 * Captures browser window and page-level events that affect the entire viewport.
 * These events track major changes in the user's environment: window resizing,
 * tab switching (visibilitychange), page focus/blur, page loading, and navigation away.
 * Critical for understanding user engagement and environmental context changes.
 */
export interface ViewportEvent {
  type:
    | 'resize' // Browser window or viewport size changed
    | 'visibilitychange' // Tab became visible/hidden (user switched tabs)
    | 'focus' // Browser window gained focus
    | 'blur' // Browser window lost focus
    | 'load' // Page finished loading
    | 'beforeunload'; // User is about to leave the page
  timestamp: number;
  viewport_width?: number; // Current viewport width in pixels
  viewport_height?: number; // Current viewport height in pixels
  visibility_state?: string; // 'visible', 'hidden', or 'prerender'
  zoom_level?: number; // Browser zoom level (1.0 = 100%)
}

/**
 * Captures input method composition events for complex text input.
 * Triggered when users type in languages that require composition (like Chinese, Japanese,
 * Korean) using Input Method Editors (IMEs), or when using features like autocomplete
 * suggestions. These events track the multi-stage process of composing complex characters
 * before they become final text input.
 */
export interface CompositionEvent {
  type: 'compositionstart' | 'compositionend' | 'compositionupdate';
  timestamp: number;
  element_index: number; // Index into ElementLog array
  data_length?: number; // Length of the composition text being built
}

/**
 * Captures device orientation and motion sensor data from mobile devices and tablets.
 * Triggered when users rotate their device, shake it, or when the device detects
 * movement through its accelerometer and gyroscope. Useful for understanding mobile
 * user behavior, detecting device orientation changes, and capturing motion-based
 * interactions in mobile web applications.
 */
export interface DeviceOrientationEvent {
  type: 'deviceorientation' | 'devicemotion';
  timestamp: number;
  alpha?: number; // Compass direction/rotation around z-axis (0-360°)
  beta?: number; // Front-to-back tilt around x-axis (-180 to 180°)
  gamma?: number; // Left-to-right tilt around y-axis (-90 to 90°)
  acceleration?: {
    // Linear acceleration excluding gravity (for devicemotion)
    x: number; // Left-right acceleration
    y: number; // Forward-backward acceleration
    z: number; // Up-down acceleration
  };
  rotation_rate?: {
    // Rotation speed in degrees per second (for devicemotion)
    alpha: number; // Rotation around z-axis
    beta: number; // Rotation around x-axis
    gamma: number; // Rotation around y-axis
  };
}

/**
 * Captures JavaScript errors and unhandled promise rejections that occur in the application.
 * Triggered automatically when JavaScript runtime errors occur (like accessing undefined
 * properties, type errors, etc.) or when promises are rejected without proper error handling.
 * Essential for monitoring application stability and identifying issues that impact user experience.
 */
export interface GlobalErrorEventLog {
  type: 'error' | 'unhandledrejection';
  timestamp: number;
  message_length?: number; // Length of error message (not the message itself for privacy)
}

/**
 * Captures touch interactions on mobile devices and touch-enabled screens.
 * Triggered by finger touches, taps, swipes, pinches, and multi-touch gestures.
 * Supports complex touch data including pressure sensitivity, touch contact area,
 * and advanced properties like altitude/azimuth angles for stylus input on
 * supported devices (like Apple Pencil or Surface Pen).
 */
export interface TouchEvent {
  type: 'touchstart' | 'touchend' | 'touchmove';
  timestamp: number;
  // ALL active touch points currently on the screen (anywhere on the page)
  // Includes every finger/stylus touching the screen, persists until all touches end
  touches: Array<{
    identifier: number; // Unique ID for this touch point
    x: number; // Touch position X coordinate
    y: number; // Touch position Y coordinate
    pressure?: number; // Pressure applied (0.0 to 1.0)
    radiusX?: number; // Horizontal radius of touch contact area
    radiusY?: number; // Vertical radius of touch contact area
    rotationAngle?: number; // Rotation of touch contact ellipse
    altitudeAngle?: number; // Angle between stylus and screen (stylus input)
    azimuthAngle?: number; // Compass direction of stylus (stylus input)
  }>;
  // ONLY touch points that changed in this specific event
  // touchstart: new touches, touchmove: moved touches, touchend: ended touches
  // This is usually what you want to process in event handlers
  changed_touches: Array<{
    identifier: number; // Touch points that changed in this event
    x: number;
    y: number;
    pressure?: number;
    radiusX?: number;
    radiusY?: number;
    rotationAngle?: number;
    altitudeAngle?: number;
    azimuthAngle?: number;
  }>;
  // ONLY active touches on the specific target element that fired this event
  // Subset of 'touches' filtered to the current element (useful for component-specific logic)
  target_touches: Array<{
    identifier: number; // Touch points currently on the target element
    x: number;
    y: number;
    pressure?: number;
    radiusX?: number;
    radiusY?: number;
    rotationAngle?: number;
    altitudeAngle?: number;
    azimuthAngle?: number;
  }>;
  element_index: number; // Index into ElementLog array
  modifiers: string[]; // Active modifier keys during touch
  touch_count: number; // Total number of simultaneous touches
}

/**
 * Captures focus changes as users navigate between interactive elements.
 * Triggered when users click into input fields, tab between form elements,
 * use keyboard navigation to focus buttons or links, or when elements lose focus.
 * Essential for tracking user attention flow and form interaction patterns.
 */
export interface FocusEvent {
  type: 'focus' | 'blur';
  timestamp: number;
  element_index: number; // Index into ElementLog array
}

/**
 * Captures form field interactions and data entry events.
 * Triggered when users type in input fields ('input' fires on every keystroke),
 * select options from dropdowns, check/uncheck boxes, or when form fields lose
 * focus after being modified ('change' fires when field value is finalized).
 * Critical for understanding form completion patterns and user input behavior.
 */
export interface FormEvent {
  type: 'input' | 'change';
  timestamp: number;
  element_index: number; // Index into ElementLog array
  value_length: number; // Length of the field value (not the actual value for privacy)
}

// Base class for all event logs with optional compact format support
abstract class BaseEventLog<T, CompactT = any[]> {
  protected events_uncompressed: T[] = [];

  protected events: CompactT[] = [];

  protected maxEvents: number;

  protected useCompactFormat: boolean;

  // Abstract method that subclasses must implement to convert to compact format
  protected abstract encodeToCompact(event: T): CompactT;

  constructor(maxEvents: number = 10000, useCompactFormat: boolean = false) {
    this.maxEvents = maxEvents;
    this.useCompactFormat = useCompactFormat;
  }

  update(event: T): void {
    if (this.useCompactFormat) {
      // Store in compact format
      const compactEvent = this.encodeToCompact(event);
      this.events.push(compactEvent);

      // Prevent memory leaks by limiting stored events
      if (this.events.length > this.maxEvents) {
        this.events = this.events.slice(-this.maxEvents);
      }
    } else {
      // Store in original format
      this.events_uncompressed.push(event);

      // Prevent memory leaks by limiting stored events
      if (this.events_uncompressed.length > this.maxEvents) {
        this.events_uncompressed = this.events_uncompressed.slice(
          -this.maxEvents
        );
      }
    }
  }

  // Get original format events (for backward compatibility)
  toArrayUncompressed(): T[] {
    return [...this.events_uncompressed];
  }

  // Get compact format events with trimmed trailing nulls
  toArrayCompact(): any[] {
    return this.events.map((event) =>
      trimTrailingNulls(event as unknown as any[])
    );
  }

  clear(): void {
    this.events = [];
    this.events_uncompressed = [];
  }

  get length(): number {
    return this.useCompactFormat
      ? this.events.length
      : this.events_uncompressed.length;
  }

  // Toggle between formats (will clear existing events)
  setCompactFormat(useCompact: boolean): void {
    if (this.useCompactFormat !== useCompact) {
      this.clear();
      this.useCompactFormat = useCompact;
    }
  }
}

// Log classes for each event type with compact format support
export class KeyboardLog extends BaseEventLog<
  KeyboardEvent,
  CompactKeyboardEvent
> {
  // eslint-disable-next-line class-methods-use-this
  protected encodeToCompact(event: KeyboardEvent): CompactKeyboardEvent {
    return encodeKeyboardEvent(event);
  }
}

export class MouseLog extends BaseEventLog<MouseEvent, CompactMouseEvent> {
  // eslint-disable-next-line class-methods-use-this
  protected encodeToCompact(event: MouseEvent): CompactMouseEvent {
    return encodeMouseEvent(event);
  }
}

export class WheelLog extends BaseEventLog<WheelEvent, CompactWheelEvent> {
  // eslint-disable-next-line class-methods-use-this
  protected encodeToCompact(event: WheelEvent): CompactWheelEvent {
    return encodeWheelEvent(event);
  }
}

export class TouchLog extends BaseEventLog<TouchEvent, CompactTouchEvent> {
  // eslint-disable-next-line class-methods-use-this
  protected encodeToCompact(event: TouchEvent): CompactTouchEvent {
    return encodeTouchEvent(event);
  }

  // Override toArrayCompact to also trim trailing nulls from CompactTouchPoint arrays
  toArrayCompact(): any[] {
    return this.events.map((event) => {
      const trimmedEvent = trimTrailingNulls(event as unknown as any[]);

      // Touch events have CompactTouchPoint arrays at indices 2, 3, 4 that also need trimming
      if (trimmedEvent[2] && Array.isArray(trimmedEvent[2])) {
        trimmedEvent[2] = trimmedEvent[2].map((touchPoint: any[]) =>
          trimTrailingNulls(touchPoint)
        );
      }
      if (trimmedEvent[3] && Array.isArray(trimmedEvent[3])) {
        trimmedEvent[3] = trimmedEvent[3].map((touchPoint: any[]) =>
          trimTrailingNulls(touchPoint)
        );
      }
      if (trimmedEvent[4] && Array.isArray(trimmedEvent[4])) {
        trimmedEvent[4] = trimmedEvent[4].map((touchPoint: any[]) =>
          trimTrailingNulls(touchPoint)
        );
      }

      return trimmedEvent;
    });
  }
}

export class FocusLog extends BaseEventLog<FocusEvent, CompactFocusEvent> {
  // eslint-disable-next-line class-methods-use-this
  protected encodeToCompact(event: FocusEvent): CompactFocusEvent {
    return encodeFocusEvent(event);
  }
}

export class FormLog extends BaseEventLog<FormEvent, CompactFormEvent> {
  // eslint-disable-next-line class-methods-use-this
  protected encodeToCompact(event: FormEvent): CompactFormEvent {
    return encodeFormEvent(event);
  }
}

export class ClipboardLog extends BaseEventLog<
  ClipboardEvent,
  CompactClipboardEvent
> {
  // eslint-disable-next-line class-methods-use-this
  protected encodeToCompact(event: ClipboardEvent): CompactClipboardEvent {
    return encodeClipboardEvent(event);
  }
}

export class SelectionLog extends BaseEventLog<
  SelectionEvent,
  CompactSelectionEvent
> {
  // eslint-disable-next-line class-methods-use-this
  protected encodeToCompact(event: SelectionEvent): CompactSelectionEvent {
    return encodeSelectionEvent(event);
  }
}

export class ViewportLog extends BaseEventLog<
  ViewportEvent,
  CompactViewportEvent
> {
  // eslint-disable-next-line class-methods-use-this
  protected encodeToCompact(event: ViewportEvent): CompactViewportEvent {
    return encodeViewportEvent(event);
  }
}

export class CompositionLog extends BaseEventLog<
  CompositionEvent,
  CompactCompositionEvent
> {
  // eslint-disable-next-line class-methods-use-this
  protected encodeToCompact(event: CompositionEvent): CompactCompositionEvent {
    return encodeCompositionEvent(event);
  }
}

export class DeviceOrientationLog extends BaseEventLog<
  DeviceOrientationEvent,
  CompactDeviceOrientationEvent
> {
  // eslint-disable-next-line class-methods-use-this
  protected encodeToCompact(
    event: DeviceOrientationEvent
  ): CompactDeviceOrientationEvent {
    return encodeDeviceOrientationEvent(event);
  }
}

export class ErrorLog extends BaseEventLog<
  GlobalErrorEventLog,
  CompactErrorEvent
> {
  // eslint-disable-next-line class-methods-use-this
  protected encodeToCompact(event: GlobalErrorEventLog): CompactErrorEvent {
    return encodeErrorEvent(event);
  }
}
